const CHECKOUT_LOADER_PATH_SUFFIX = "/bc-checkout-shop-app/bc-checkout-shop-app.js";

function isShopifyStorefrontHost(host?: string | null): boolean {
  return typeof host === "string" && host.toLowerCase().endsWith(".myshopify.com");
}

function isShopifyPreviewHost(host?: string | null): boolean {
  return typeof host === "string" && host.toLowerCase().endsWith(".shopifypreview.com");
}

function isEmbeddedCheckoutScriptUrl(scriptSrc?: string | null): boolean {
  if (!scriptSrc) {
    return false;
  }

  try {
    const url = new URL(scriptSrc);
    return url.pathname.endsWith(CHECKOUT_LOADER_PATH_SUFFIX) && url.searchParams.has("id");
  } catch {
    return false;
  }
}

/**
 * Back-room "routePath" knob (Storefront Concierge Plan 3 / D-14).
 * When served on a brand's Shopify domain under a sub-path, the proxy skeleton
 * (or intentional Liquid inject) sets `window.__BC_ROUTE_PATH__`; the widget
 * API base must include it so calls go through the App-Proxy forwarder
 * instead of escaping to a bare `/api-rewards/*` (→ Shopify 404).
 *
 * Absent → '' → front-room. Never invent `/a/sos` from a cross-origin checkout
 * script — classic theme embeds on shopperos_domain stores (e.g. petarmor.com)
 * share that layout and must keep resolving to the script origin.
 *
 * Must be defined before HOST_DOMAIN — that const is evaluated at module load
 * and calls getRoutePath().
 */
const getRoutePath = (): string => {
  if (typeof window === "undefined") return "";
  const raw = (window as unknown as { __BC_ROUTE_PATH__?: string }).__BC_ROUTE_PATH__;
  if (typeof raw === "string") {
    const trimmed = raw.trim().replace(/\/+$/, "");
    if (trimmed && trimmed !== "/") {
      return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
    }
  }
  return "";
};

/**
 * Domain utilities for bc-widgets.
 *
 * Embedded checkout contexts resolve from the checkout loader script URL.
 * Back-room Shopify surfaces (routePath injected) use the page origin for API.
 */
const getHostDomain = (): string => {
  const routePath = getRoutePath();
  if (routePath) {
    return window.location.origin;
  }

  const scripts = Array.from(document.getElementsByTagName("script"));
  const checkoutScript = scripts.find((script) => isEmbeddedCheckoutScriptUrl(script.src));

  if (checkoutScript?.src) {
    return new URL(checkoutScript.src).origin;
  }

  const currentHostname = window.location.hostname;
  if (isShopifyStorefrontHost(currentHostname) || isShopifyPreviewHost(currentHostname)) {
    throw new Error(
      "[brandclub] Could not resolve host domain for an embedded Shopify storefront. Ensure the checkout bundle is loaded from /bc-checkout-shop-app/bc-checkout-shop-app.js?id=<slug>."
    );
  }

  return window.location.origin;
};

export const HOST_DOMAIN = getHostDomain();

/**
 * The API base URL for all widget API calls.
 * Points to the api-rewards endpoint on the host domain (under the back-room
 * routePath when present).
 *
 * @example
 * // If HOST_DOMAIN is "https://shop.gund.com"
 * // API_BASE will be "https://shop.gund.com/api-rewards"
 * // Back-room: "https://store.mybrand.rocks/a/sos/api-rewards"
 */
export const API_BASE = `${HOST_DOMAIN}${getRoutePath()}/api-rewards`;

/**
 * The APPID is the domain name without the protocol.
 * For example, if the HOST_DOMAIN is "https://shop.gund.com",
 * the APPID will be "shop.gund.com".
 */
export const APPID = HOST_DOMAIN.replace(/^https?:\/\//, "");
